<script lang="ts">
	import {
		Checkbox,
		CheckboxSize,
		Chip,
		JsonDisplay,
		Row,
		rowActions
	} from '@repo/commons/components'
	import { parseJSON } from './utils.ts'
	import { keyify, isISODateStr, dateToReadableString } from '@repo/commons/utils'
	import { t } from 'svelte-i18n'
	import type { Component } from 'svelte'
	import CellDataView from './CellDataView.svelte'

	type Props = {
		enableRowSelect?: boolean
		selectedRowsSet?: Set<any>
		row: any
		columns: string[]
		columnsToSplit?: Record<string, string>
		columnStyles?: Record<string, string>
		editableColumns: string[]
		jsonColumns?: string[]
		locale?: string
		focusedRowId: string | null
		columnComponents?: Record<string, Component>
		shouldHighlightCurlyBraces?: boolean
		handleCheckboxChange: (checked: boolean, id: number) => void
		onRowDbClick: ((row: any) => void) | null
		onHandleRowFocus: (rowId: string) => void
		onHandleRowBlur: () => void
		onsubmit: ((row: Record<string, string>) => void) | null
		onActionHandler: ((action: rowActions, id: string) => void) | null
		onHandleStartEditing: () => void
	}
	const {
		enableRowSelect = true,
		selectedRowsSet = new Set<any>(),
		row,
		columns,
		columnsToSplit = {},
		columnStyles = {},
		editableColumns = [],
		jsonColumns = [],
		locale = 'de',
		focusedRowId = null,
		columnComponents = {},
		shouldHighlightCurlyBraces = false,
		handleCheckboxChange,
		onRowDbClick = null,
		onHandleRowFocus,
		onHandleRowBlur,
		onsubmit = null,
		onActionHandler = null,
		onHandleStartEditing
	}: Props = $props()
</script>

{#if enableRowSelect}
	<div class="check-cell box">
		<Checkbox
			id={row.id.toString()}
			labelExternalStyle="margin-bottom: unset;"
			size={CheckboxSize.SMALL}
			checked={selectedRowsSet.has(row.id)}
			onchange={handleCheckboxChange}
		/>
	</div>
{/if}

{#each columns as column (column)}
	{#if columnsToSplit[column] && row[column]}
		<CellDataView
			style={columnStyles[column] ?? columnStyles['*']}
			onDblClick={() => {
				if (!editableColumns?.includes(column)) {
					onRowDbClick?.(row)
				}
			}}
		>
			<div class="label">
				{#each row[column].split(columnsToSplit[column]) as item (item)}
					<Chip text={item} removable={false} customClass="json-chip" />
				{/each}
			</div>
		</CellDataView>
	{:else if jsonColumns.includes(column)}
		<CellDataView
			style={columnStyles[column] ?? columnStyles['*']}
			onDblClick={() => {
				if (!editableColumns?.includes(column)) {
					onRowDbClick?.(row)
				}
			}}
		>
			<div class="label">
				{#if row[column]}
					<JsonDisplay
						object={parseJSON(row[column]) as any}
						keys={keyify(parseJSON(row[column]))}
					/>
				{:else}
					{$t('no_entity_schema_defined')}
				{/if}
			</div>
		</CellDataView>
	{:else if isISODateStr(row[column])}
		<CellDataView
			style={columnStyles[column] ?? columnStyles['*']}
			onDblClick={() => {
				if (!editableColumns?.includes(column)) {
					onRowDbClick?.(row)
				}
			}}
		>
			<div class="label">
				{dateToReadableString(row[column], locale)}
			</div>
		</CellDataView>
	{:else}
		<CellDataView
			isEditable={editableColumns?.includes(column)}
			isFocused={focusedRowId === row.id}
			style={columnStyles[column] ?? columnStyles['*']}
			onDblClick={() => {
				if (!editableColumns?.includes(column)) {
					onRowDbClick?.(row)
				}
			}}
		>
			{#if columnComponents[column] && row}
				{@const SvelteComponent = columnComponents[column]}
				<SvelteComponent
					{onActionHandler}
					{onsubmit}
					onfocus={() => onHandleRowFocus(row.id)}
					onblur={onHandleRowBlur}
					editableEnabled={editableColumns?.includes(column)}
					{row}
					key={column}
					{shouldHighlightCurlyBraces}
					onstartediting={onHandleStartEditing}
				/>
			{:else}
				<div class="label">
					<Row
						editableEnabled={editableColumns?.includes(column)}
						{row}
						{column}
						onfocus={() => onHandleRowFocus(row.id)}
						onblur={onHandleRowBlur}
						{onsubmit}
						{shouldHighlightCurlyBraces}
						onstartediting={() => onHandleStartEditing()}
					/>
				</div>
			{/if}
		</CellDataView>
	{/if}
{/each}

<style lang="postcss">
	.check-cell.box {
		color: #000;
		padding: 10px;
		font-size: 150%;
		font-size: 12px;
		border-bottom: 1px solid #e4e7ed;
		display: flex;
		align-items: center;
	}

	div.label {
		font-size: 16px;
		font-weight: 400;
		line-height: 23px;
		color: #606266;
		border: none;
		outline: none;
	}

	:global(.selection-checkbox-column) {
		min-width: 20px;
		width: 24px !important;
		height: 24px !important;
	}

	:global(.json-chip) {
		background: #dcdfe6;
		border-radius: 8px;
		font-size: 0.625rem;
		color: #07343d;
		line-height: 15px;
		padding: 5px 8px;
	}
</style>
